import { createAsyncThunk } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { AuthRepositoryImpl } from '../Data/repositories/AuthRepositoryImpl';
import { LoginUseCase } from '../Domain/UseCase/LoginUseCase';
import { MeUseCase } from '../Domain/UseCase/MeUseCase';
import { RefreshTokenUseCase } from '../Domain/UseCase/RefreshTokenUseCase';
import {
  clearSlotOpsOverrideToken,
  getSlotOpsOverrideToken,
  setSlotOpsOverrideToken,
} from '../../../Utils/slotOpsOverride';
import { logout } from './reducers';

const repo = new AuthRepositoryImpl();

export const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ username, password }: { username: string; password: string }, { dispatch }) => {
    // 기존 로컬 토큰/상태 초기화 후 새 로그인 시도
    // 로그아웃을 먼저 수행하여 Redux 상태(특히 이전 계정 정보)를 완전히 비움
    dispatch(logout());
    
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    return new LoginUseCase(repo).execute(username, password);
  },
);

export const initAuthThunk = createAsyncThunk('auth/init', async () => {
  // 로그인 직후 토큰 저장 타이밍 이슈를 완화하기 위해 약간 대기
  await new Promise(resolve => setTimeout(resolve, 500));
  const acc = localStorage.getItem('access');
  const ref = localStorage.getItem('refresh');
  if (!acc || !ref) return null;
  const user = await new MeUseCase(repo).execute();
  if (getSlotOpsOverrideToken() && !user.slot_ops_override?.active) {
    clearSlotOpsOverrideToken();
  }
  return { access: acc, refresh: ref, user };
});

export const refreshTokenThunk = createAsyncThunk('auth/refresh', async () => {
  const refresh = localStorage.getItem('refresh');
  if (!refresh) throw Error();
  const { access } = await new RefreshTokenUseCase(repo).execute(refresh);
  return access;
});

export const fetchMeThunk = createAsyncThunk('auth/fetchMe', async () => {
  return new MeUseCase(repo).execute();
});

export const activateSlotOpsOverrideThunk = createAsyncThunk(
  'auth/activateSlotOpsOverride',
  async (password: string, { dispatch, rejectWithValue }) => {
    try {
      const result = await repo.activateSlotOpsOverride(password);
      setSlotOpsOverrideToken(result.override_token);
      const user = await dispatch(fetchMeThunk()).unwrap();
      return { ...result, user };
    } catch (error) {
      clearSlotOpsOverrideToken();
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 403) {
          return rejectWithValue('비밀번호가 올바르지 않거나 권한이 없습니다.');
        }
        if (status === 503) {
          return rejectWithValue('서버에 임시 해제 기능이 설정되어 있지 않습니다.');
        }
      }
      return rejectWithValue('임시 해제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    }
  },
);

export const clearExpiredSlotOpsOverrideThunk = createAsyncThunk(
  'auth/clearExpiredSlotOpsOverride',
  async (_, { dispatch }) => {
    clearSlotOpsOverrideToken();
    return dispatch(fetchMeThunk()).unwrap();
  },
);
